import React, { useState, useEffect } from 'react';
import { Table, DropdownButton, Form, Container, Row, Col, Card, Badge, Spinner, Button, Dropdown, OverlayTrigger, Tooltip, InputGroup, FormControl, Modal } from 'react-bootstrap';
import api from '../../services/api';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import { FaSearch, FaSave, FaEdit, FaTimes, FaFilter, FaSortAmountUp, FaSortAmountDown, FaInfoCircle, FaDownload, FaPlus } from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import { API_COLORS } from '../../constants/colors';

const LeaveQuotaUsage = () => {
    const [employees, setEmployees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: 'asc'
    });
    const [filters, setFilters] = useState({
        departments: [],
        leaveTypes: []
    });
    const [editing, setEditing] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [newRow, setNewRow] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const fetchEmployeeQuotaUsage = async () => {
        try {
            const response = await api.get(`/leave/quotas/usage?year=${selectedYear}`);
            setEmployees(response.data);
        } catch (error) {
            console.error('Error fetching employee quota usage:', error);
        }
    };

    const startEditing = (employeeId, quotaId) => {
        setEditing({ employeeId, quotaId });
        const employee = employees.find(e => e.id === employeeId);
        const quota = employee?.quotas.find(q => q.leave_type_id === quotaId);
        if (quota) {
            setEditValues({
                allocated: quota.allocated,
                used: quota.used
            });
        }
    };

    const handleEditChange = (field, value, isNewRow = false) => {
        if (isNewRow) {
            setNewRow(prev => ({
                ...prev,
                [field]: field === 'allocated' || field === 'used' ? parseFloat(value) || 0 : value
            }));
        } else {
            setEditValues(prev => ({
                ...prev,
                [field]: field === 'allocated' || field === 'used' ? parseFloat(value) || 0 : value
            }));
        }
    };

    const saveEdit = async () => {
        try {
            setIsSaving(true);
            const { employeeId, quotaId, isNew } = editing;
            
            if (isNew) {
                // Create new quota
                await api.post('/leave/quotas/setup', {
                    employee_id: employeeId,
                    year: selectedYear,
                    quotas: [{
                        leave_type_id: newRow.leaveTypeId,
                        allocated: newRow.allocated || 0,
                        used: newRow.used || 0
                    }]
                });
                setNewRow(null);
            } else {
                // Update existing quota
                const allocated = parseFloat(editValues.allocated) || 0;
                const used = parseFloat(editValues.used) || 0;
                
                await api.put(`/leave/quotas/${employeeId}/${quotaId}?year=${selectedYear}`, {
                    allocated: allocated,
                    used: used
                });
            }

            // Refresh the data after successful update
            await fetchEmployeeQuotaUsage();
            
            setEditing(null);
            setEditValues({});
        } catch (error) {
            console.error('Error saving quota:', error);
            alert('Failed to save quota changes');
        } finally {
            setIsSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditing(null);
        setEditValues({});
        setNewRow(null);
    };

    const startNewRow = () => {
        setNewRow({
            employeeId: '',
            leaveTypeId: '',
            allocated: 0,
            used: 0
        });
        setEditing({
            employeeId: '',
            quotaId: 'new',
            isNew: true
        });
    };

    useEffect(() => {
        fetchEmployeeQuotaUsage();
        fetchLeaveTypes();
    }, [selectedYear]);

    const fetchLeaveTypes = async () => {
        try {
            const response = await api.get('/leave-types');
            setLeaveTypes(response.data);
        } catch (error) {
            console.error('Error fetching leave types:', error);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleFilter = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMultiSelect = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: prev[field].includes(value) 
                ? prev[field].filter(item => item !== value) 
                : [...prev[field], value]
        }));
    };

    const filteredEmployees = employees.filter(employee => {
        const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDepartment = filters.departments.length === 0 || filters.departments.includes(employee.department);
        
        // If no leave type filters, include all employees
        if (filters.leaveTypes.length === 0) {
            return matchesSearch && matchesDepartment;
        }
        
        // Otherwise check if any quota matches the selected leave types
        return matchesSearch && 
               matchesDepartment && 
               employee.quotas.some(quota => filters.leaveTypes.includes(quota.leave_type_name));
    });

    const sortedEmployees = filteredEmployees.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortConfig.direction === 'asc' 
                ? aValue.localeCompare(bValue) 
                : bValue.localeCompare(aValue);
        }
        
        return sortConfig.direction === 'asc' 
            ? aValue - bValue 
            : bValue - aValue;
    });

    const headers = [
        { label: 'Employee Name', key: 'name' },
        // { label: 'Department', key: 'department' },
        { label: 'Leave Type', key: 'leave_type_name' },
        { label: 'Allocated', key: 'allocated' },
        { label: 'Used', key: 'used' },
        { label: 'Remaining', key: 'remaining' },
        { label: '% Used', key: 'percentage_used' }
    ];

    const csvData = sortedEmployees.flatMap(employee => 
        employee.quotas.map(quota => ({
            ...employee,
            ...quota,
            id: employee.id
        }))
    );

    return (
        <Layout>
            <TopBar >
            <div className="col-md-4">
                    <InputGroup className="mb-3">
                        <InputGroup.Text id="basic-addon1">
                            <FaSearch />
                        </InputGroup.Text>
                        <FormControl
                            placeholder="Search employees..."
                            aria-label="Search employees"
                            aria-describedby="basic-addon1"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </InputGroup>
                </div>
            </TopBar>
        <div className="container-fluid mt-4" > 
           
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-2">
                    <DropdownButton
                        title={`Departments: ${filters.departments.length > 0 ? filters.departments.join(', ') : 'All'}`}
                        onSelect={(value) => handleMultiSelect('departments', value)}
                    >
                        <Dropdown.Item onSelect="">All Departments</Dropdown.Item>
                        {employees
                            .map(emp => emp.department)
                            .filter((value, index, self) => self.indexOf(value) === index)
                            .map(dept => (
                                <Dropdown.Item 
                                    key={dept} 
                                    onSelect={dept}
                                    active={filters.departments.includes(dept)}
                                >
                                    {dept}
                                </Dropdown.Item>
                            ))}
                    </DropdownButton>
                    <DropdownButton
                        title={`Year: ${selectedYear}`}
                        onSelect={(value) => setSelectedYear(parseInt(value))}
                    >
                        <Dropdown.Item eventKey={new Date().getFullYear()} active={selectedYear === new Date().getFullYear()}>
                            {new Date().getFullYear()}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey={new Date().getFullYear() - 1} active={selectedYear === new Date().getFullYear() - 1}>
                            {new Date().getFullYear() - 1}
                        </Dropdown.Item>
                        <Dropdown.Item eventKey={new Date().getFullYear() - 2} active={selectedYear === new Date().getFullYear() - 2}>
                            {new Date().getFullYear() - 2}
                        </Dropdown.Item>
                    </DropdownButton>
                    {/* Leave type filter removed as requested */}
                </div>
                <div className="d-flex gap-2">
                    <CSVLink data={csvData} headers={headers} filename={`leave_quota_usage_${selectedYear}.csv`} className="btn btn-primary d-flex align-items-center">
                        <FaDownload className="me-1" /> Export to CSV
                    </CSVLink>
                </div>
            </div>

            <div className="table-responsive" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>S. No.</th>
                            <th>Employee Name</th>
                            <th onClick={() => handleSort('department')}>
                                Department
                                {sortConfig.key === 'department' && (
                                    <span>
                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </th>
                            <th onClick={() => handleSort('leave_type_name')}>
                                Leave Type
                                {sortConfig.key === 'leave_type_name' && (
                                    <span>
                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </th>
                            <th onClick={() => handleSort('allocated')}>
                                Allocated
                                {sortConfig.key === 'allocated' && (
                                    <span>
                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </th>
                            <th onClick={() => handleSort('used')}>
                                Used
                                {sortConfig.key === 'used' && (
                                    <span>
                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </th>
                            <th onClick={() => handleSort('remaining')}>
                                Remaining
                                {sortConfig.key === 'remaining' && (
                                    <span>
                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </th>
                            <th onClick={() => handleSort('percentage_used')}>
                                % Used
                                {sortConfig.key === 'percentage_used' && (
                                    <span>
                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                    </span>
                                )}
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            let serial = 1;
                            let rows = [];
                            // Add new row at the top if in edit mode
                            if (newRow && editing?.isNew) {
                                rows.push(
                                    <tr key="new-row" className="table-warning">
                                        <td>New</td>
                                        <td>
                                            <Form.Select
                                                value={newRow.employeeId}
                                                onChange={(e) => handleEditChange('employeeId', e.target.value, true)}
                                                className="w-auto"
                                            >
                                                <option value="">Select Employee</option>
                                                {employees.map(emp => (
                                                    <option key={emp.id} value={emp.id}>
                                                        {emp.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </td>
                                        <td>
                                            {employees.find(e => e.id === newRow.employeeId)?.department || ''}
                                        </td>
                                        <td>
                                            <Form.Select
                                                value={newRow.leaveTypeId}
                                                onChange={(e) => handleEditChange('leaveTypeId', e.target.value, true)}
                                                className="w-auto"
                                            >
                                                <option value="">Select Leave Type</option>
                                                {leaveTypes.map(type => (
                                                    <option key={type.id} value={type.id}>
                                                        {type.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                step="0.1"
                                                value={newRow.allocated}
                                                onChange={(e) => handleEditChange('allocated', e.target.value, true)}
                                                className="w-auto"
                                                min="0"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                value={newRow.used}
                                                onChange={(e) => handleEditChange('used', e.target.value, true)}
                                                className="w-auto"
                                                step="0.1"
                                                min="0"
                                                max={newRow.allocated}
                                            />
                                        </td>
                                        <td>
                                            {(newRow.allocated - newRow.used).toFixed(1)}
                                        </td>
                                        <td>
                                            {newRow.allocated > 0 ? 
                                                Math.round((newRow.used / newRow.allocated) * 100) : 0}%
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button 
                                                    variant="outline-success" 
                                                    size="sm" 
                                                    onClick={saveEdit}
                                                    className="d-flex align-items-center"
                                                    title="Save changes"
                                                    disabled={isSaving || !newRow.employeeId || !newRow.leaveTypeId}
                                                >
                                                    {isSaving ? (
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        <FaSave />
                                                    )}
                                                </Button>
                                                <Button 
                                                    variant="outline-secondary" 
                                                    size="sm" 
                                                    onClick={cancelEdit}
                                                    className="d-flex align-items-center"
                                                    title="Cancel"
                                                    disabled={isSaving}
                                                >
                                                    <FaTimes />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }

                            // Existing employee rows
                            sortedEmployees.forEach(employee => {
                                // Include all quotas, even those with zero allocation
                                const filteredQuotas = employee.quotas || [];
                                filteredQuotas.forEach((quota, idx) => {
                                    rows.push(
                                        <tr key={`${employee.id}-${quota.leave_type_id}`}>
                                            <td>{idx === 0 ? serial++ : ''}</td>
                                            <td>{idx === 0 ? employee.name : ''}</td>
                                            <td>{employee.department}</td>
                                            <td>
                                                {editing?.employeeId === employee.id && editing?.quotaId === quota.leave_type_id ? (
                                                    <Form.Control
                                                        type="text"
                                                        value={quota.leave_type_name}
                                                        disabled
                                                        className="w-auto"
                                                    />
                                                ) : (
                                                    quota.leave_type_name
                                                )}
                                            </td>
                                            <td>
                                                {editing?.employeeId === employee.id && editing?.quotaId === quota.leave_type_id ? (
                                                    <Form.Control
                                                        type="number"
                                                        step="0.1"
                                                        value={editValues.allocated}
                                                        onChange={(e) => handleEditChange('allocated', e.target.value)}
                                                        className="w-auto"
                                                        min="0"
                                                    />
                                                ) : (
                                                    parseFloat(quota.allocated).toFixed(1)
                                                )}
                                            </td>
                                            <td>
                                                {editing?.employeeId === employee.id && editing?.quotaId === quota.leave_type_id ? (
                                                    <Form.Control
                                                        type="number"
                                                        value={editValues.used}
                                                        onChange={(e) => handleEditChange('used', e.target.value)}
                                                        className="w-auto"
                                                        step="0.1"
                                                    />
                                                ) : (
                                                    parseFloat(quota.used).toFixed(1)
                                                )}
                                            </td>
                                            <td>
                                                {editing?.employeeId === employee.id && editing?.quotaId === quota.leave_type_id ? (
                                                    <span>{editValues.allocated - editValues.used}</span>
                                                ) : (
                                                    parseFloat(quota.remaining).toFixed(1)
                                                )}
                                            </td>
                                            <td>
                                                {editing?.employeeId === employee.id && editing?.quotaId === quota.leave_type_id ? (
                                                    <span>
                                                        {editValues.allocated > 0 ? 
                                                            Math.round((editValues.used / editValues.allocated) * 100) : 0}%
                                                    </span>
                                                ) : (
                                                    <Badge bg={
                                                        quota.percentage_used >= 80 ? "danger" :
                                                        quota.percentage_used >= 50 ? "warning" :
                                                        "success"
                                                    }>
                                                        {quota.percentage_used}%
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                {editing?.employeeId === employee.id && editing?.quotaId === quota.leave_type_id ? (
                                                    <div className="d-flex gap-2">
                                                        <Button 
                                                            variant="outline-success" 
                                                            size="sm" 
                                                            onClick={saveEdit} 
                                                            className="d-flex align-items-center"
                                                            title="Save changes"
                                                            disabled={isSaving}
                                                        >
                                                            {isSaving ? (
                                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                            ) : (
                                                                <FaSave />
                                                            )}
                                                        </Button>
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            size="sm" 
                                                            onClick={cancelEdit}
                                                            className="d-flex align-items-center"
                                                            title="Cancel editing"
                                                            disabled={isSaving}
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm" 
                                                        onClick={() => startEditing(employee.id, quota.leave_type_id)}
                                                        className="d-flex align-items-center"
                                                        title="Edit quota"
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                });
                            });
                            return rows;
                        })()}
                    </tbody>
                </Table>
            </div>
        </div>

        </Layout>
    );
};

export default LeaveQuotaUsage;
